import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RefreshCw, Save, Check, AlertTriangle, Search, Image as ImageIcon, X, Upload, Info } from 'lucide-react';
import {
  bcApi, fetchAllPages, inBatches, readFileAsBase64,
  UPLOAD_CATEGORY_URL, MAX_UPLOAD_BYTES, BASE_URL,
} from './siteContentShared';
import { getActiveEnv } from '../../services/env';
import { getAccessToken } from '../../services/auth';
import PageBlocksEditor from './PageBlocksEditor';
import BannerSizingInfo from './BannerSizingInfo';
import DropZone from './DropZone';
import CustomPagesSection from './CustomPagesSection';

const NAMESPACE = 'banner';
const KEYS = ['title', 'subhead'];

async function loadBannerMetafields(categoryId) {
  const res = await bcApi('GET', `/v3/catalog/categories/${categoryId}/metafields?namespace=${NAMESPACE}&limit=50`);
  const byKey = {};
  for (const mf of (res?.data || [])) {
    if (KEYS.includes(mf.key)) byKey[mf.key] = { id: mf.id, value: mf.value || '' };
  }
  return byKey;
}

async function upsertBannerMetafield(categoryId, key, value, existingId) {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    if (existingId) {
      await bcApi('DELETE', `/v3/catalog/categories/${categoryId}/metafields/${existingId}`);
    }
    return null;
  }
  const body = {
    key,
    value: trimmed,
    namespace: NAMESPACE,
    permission_set: 'read_and_sf_access',
  };
  if (existingId) {
    const res = await bcApi('PUT', `/v3/catalog/categories/${categoryId}/metafields/${existingId}`, body);
    return { id: res?.data?.id || existingId, value: trimmed };
  }
  const res = await bcApi('POST', `/v3/catalog/categories/${categoryId}/metafields`, body);
  return { id: res?.data?.id, value: trimmed };
}

async function updateCategoryImage(categoryId, imageUrl) {
  await bcApi('PUT', `/v3/catalog/categories/${categoryId}`, { image_url: imageUrl || '' });
}

function defaultSubheadFromDescription(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  const first = div.firstElementChild || div;
  const text = (first.textContent || '').replace(/\s+/g, ' ').trim();
  return text.length > 140 ? text.slice(0, 137) + '...' : text;
}

// upload-category-image is admin-gated (PR #56); a token-less request 401s with a
// cryptic "Unauthorized". Surface a clear re-login prompt instead.
const SESSION_EXPIRED_MSG = 'Your session expired — please sign in again.';

async function uploadCategoryImage(categoryId, file) {
  const dataUrl = await readFileAsBase64(file);
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_EXPIRED_MSG);
  const res = await fetch(UPLOAD_CATEGORY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      categoryId,
      env: getActiveEnv(),
      imageBase64: dataUrl,
      filename: file.name,
      contentType: file.type || 'image/jpeg',
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error(SESSION_EXPIRED_MSG);
  if (!res.ok) throw new Error(data?.error || 'Upload failed');
  return data.image_url;
}

export default function SiteContentCategoryTab() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [status, setStatus] = useState(null);
  const [search, setSearch] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [blocksModal, setBlocksModal] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => { if (e.key === 'Escape') setPreview(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setStatus(null);
    try {
      setLoadingMsg('Loading categories from BigCommerce...');
      const cats = await fetchAllPages('/v3/catalog/categories');
      setLoadingMsg(`Loading banner metafields for ${cats.length} categories...`);
      const withMeta = await inBatches(cats, 6, async (c) => {
        try {
          const mf = await loadBannerMetafields(c.id);
          return { ...c, banner: mf };
        } catch (e) {
          return { ...c, banner: {}, error: e.message };
        }
      });
      withMeta.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setRows(withMeta);
      setEdits({});
    } catch (e) {
      setStatus({ type: 'error', msg: e.message });
    } finally {
      setLoading(false);
      setLoadingMsg('');
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const getEdit = (row, field) => {
    const e = edits[row.id];
    if (e && field in e) return e[field];
    if (field === 'imageUrl') return row.image_url || '';
    return row.banner?.[field]?.value || '';
  };

  const setEdit = (id, field, value) => {
    setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const isRowDirty = (id) => {
    const e = edits[id];
    return e && Object.keys(e).length > 0;
  };

  const resetRow = (id) => {
    setEdits(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const saveRow = async (row) => {
    const e = edits[row.id];
    if (!e) return;
    setSavingId(row.id);
    setStatus(null);
    try {
      const nextBanner = { ...(row.banner || {}) };
      if ('title' in e) {
        const r = await upsertBannerMetafield(row.id, 'title', e.title, row.banner?.title?.id);
        if (r) nextBanner.title = r; else delete nextBanner.title;
      }
      if ('subhead' in e) {
        const r = await upsertBannerMetafield(row.id, 'subhead', e.subhead, row.banner?.subhead?.id);
        if (r) nextBanner.subhead = r; else delete nextBanner.subhead;
      }
      let nextImageUrl = row.image_url;
      if ('imageUrl' in e) {
        await updateCategoryImage(row.id, e.imageUrl);
        nextImageUrl = e.imageUrl;
      }
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, banner: nextBanner, image_url: nextImageUrl } : r));
      resetRow(row.id);
      setStatus({ type: 'ok', msg: `Saved: ${row.name}` });
    } catch (err) {
      setStatus({ type: 'error', msg: `Save failed for ${row.name}: ${err.message}` });
    } finally {
      setSavingId(null);
    }
  };

  const openFilePicker = (categoryId) => {
    uploadTargetRef.current = categoryId;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const uploadFileForCategory = async (categoryId, file) => {
    if (!file || !categoryId) return;
    if (!file.type.startsWith('image/')) {
      setStatus({ type: 'error', msg: 'Please pick an image file.' });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setStatus({ type: 'error', msg: `Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.` });
      return;
    }
    setUploadingId(categoryId);
    setStatus(null);
    try {
      const imageUrl = await uploadCategoryImage(categoryId, file);
      if (!imageUrl) throw new Error('Upload succeeded but BC returned no image_url');
      setRows(prev => prev.map(r => r.id === categoryId ? { ...r, image_url: imageUrl } : r));
      setEdits(prev => {
        const next = { ...prev };
        if (next[categoryId]) {
          const { imageUrl: _drop, ...rest } = next[categoryId];
          if (Object.keys(rest).length === 0) delete next[categoryId]; else next[categoryId] = rest;
        }
        return next;
      });
      setStatus({ type: 'ok', msg: 'Image uploaded to BigCommerce.' });
    } catch (err) {
      setStatus({ type: 'error', msg: `Upload failed: ${err.message}` });
    } finally {
      setUploadingId(null);
    }
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    const categoryId = uploadTargetRef.current;
    uploadTargetRef.current = null;
    await uploadFileForCategory(categoryId, file);
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r => (r.name || '').toLowerCase().includes(s));
  }, [rows, search]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <p style={{ color: 'var(--gray-500)', margin: 0, fontSize: 13 }}>
          Control the banner title, subhead, and image on each storefront category page.
          Leave a field blank to fall back to BigCommerce defaults (category name, no subhead, existing image).
        </p>
        <button
          onClick={loadAll}
          disabled={loading}
          style={{ padding: '8px 14px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'Loading...' : 'Reload'}
        </button>
      </div>

      {loading && loadingMsg && <div style={{ color: 'var(--gray-500)', fontSize: 13, marginBottom: 12 }}>{loadingMsg}</div>}

      {status && (
        <div style={{ margin: '12px 0', padding: '10px 12px', borderRadius: 6, background: status.type === 'error' ? '#fee' : '#e8f5e9', color: status.type === 'error' ? '#b00' : '#2a7', display: 'flex', alignItems: 'center', gap: 8 }}>
          {status.type === 'error' ? <AlertTriangle size={16} /> : <Check size={16} />}
          <span style={{ fontSize: 13 }}>{status.msg}</span>
        </div>
      )}

      <div style={{ margin: '8px 0 16px', padding: '10px 12px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a', display: 'flex', gap: 10, alignItems: 'center', fontSize: 12.5, flexWrap: 'wrap' }}>
        <Info size={16} style={{ flexShrink: 0 }} />
        <span>
          Max upload 5 MB. Uploading replaces the image immediately on BigCommerce; no Save needed for image-only changes.
        </span>
        <BannerSizingInfo style={{ marginLeft: 'auto', color: '#1e3a8a' }} />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChosen}
        style={{ display: 'none' }}
      />

      <CustomPagesSection setStatus={setStatus} setPreview={setPreview} />

      <div style={{ marginBottom: 12, position: 'relative', maxWidth: 360 }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
        <input
          type="text"
          placeholder="Search categories..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '8px 10px 8px 32px', width: '100%', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 13 }}
        />
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: 'var(--gray-100)', textAlign: 'left' }}>
            <tr>
              <th style={th}>Category</th>
              <th style={{ ...th, minWidth: 280 }}>Image</th>
              <th style={th}>Banner Title override</th>
              <th style={th}>Banner Subhead override</th>
              <th style={th}>Image URL</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(row => {
              const dirty = isRowDirty(row.id);
              const titleVal = getEdit(row, 'title');
              const subheadVal = getEdit(row, 'subhead');
              const imageVal = getEdit(row, 'imageUrl');
              const saving = savingId === row.id;
              return (
                <tr key={row.id} style={{ borderTop: '1px solid var(--gray-200)', background: dirty ? '#fff8e1' : '#fff' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{row.name}</div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 11 }}>id: {row.id}</div>
                    <button type="button" onClick={() => setBlocksModal({ name: row.name, key: row.custom_url && row.custom_url.url })} style={{ marginTop: 4, fontSize: 11, padding: '3px 7px', border: '1px solid #2563eb', color: '#2563eb', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>Content Blocks (DB)</button>
                  </td>
                  <td style={td}>
                    <DropZone
                      disabled={uploadingId === row.id}
                      onFile={(file) => uploadFileForCategory(row.id, file)}
                      label={uploadingId === row.id ? 'Uploading...' : 'Drop image to upload'}
                      style={{ display: 'inline-block' }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {row.image_url ? (
                          <button
                            type="button"
                            onClick={() => setPreview({ url: row.image_url, name: row.name })}
                            title="Click to preview, or drag an image here to replace"
                            style={{ width: 320, height: 60, padding: 0, border: '1px solid var(--gray-200)', borderRadius: 4, background: '#f7f8fa', cursor: 'zoom-in', display: 'block', overflow: 'hidden' }}
                          >
                            <img
                              src={row.image_url}
                              alt=""
                              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                            />
                          </button>
                        ) : (
                          <div style={{ width: 320, height: 60, border: '1px dashed var(--gray-300)', borderRadius: 4, color: 'var(--gray-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 4, fontSize: 11, background: '#fafafa' }}>
                            <ImageIcon size={18} />
                            <span>No image — drag one here or click Upload</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button
                            onClick={() => openFilePicker(row.id)}
                            disabled={uploadingId === row.id}
                            style={{ padding: '4px 8px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: uploadingId === row.id ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                          >
                            <Upload size={11} /> {uploadingId === row.id ? 'Uploading...' : 'Upload'}
                          </button>
                          <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>or drag &amp; drop</span>
                        </div>
                      </div>
                    </DropZone>
                  </td>
                  <td style={td}>
                    <input
                      type="text"
                      placeholder={row.name}
                      value={titleVal}
                      onChange={(e) => setEdit(row.id, 'title', e.target.value)}
                      style={input}
                    />
                  </td>
                  <td style={td}>
                    <input
                      type="text"
                      placeholder={defaultSubheadFromDescription(row.description) || '(no default — blank unless you set one)'}
                      value={subheadVal}
                      onChange={(e) => setEdit(row.id, 'subhead', e.target.value)}
                      style={input}
                      title={defaultSubheadFromDescription(row.description) ? `Current default (from category description): ${defaultSubheadFromDescription(row.description)}` : 'No category description set — subhead will be blank unless you enter one.'}
                    />
                  </td>
                  <td style={td}>
                    <input
                      type="text"
                      placeholder="https://..."
                      value={imageVal}
                      onChange={(e) => setEdit(row.id, 'imageUrl', e.target.value)}
                      style={input}
                    />
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {dirty && (
                      <>
                        <button
                          onClick={() => saveRow(row)}
                          disabled={saving}
                          style={{ padding: '6px 10px', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                        >
                          <Save size={12} /> {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => resetRow(row.id)}
                          disabled={saving}
                          style={{ padding: '6px 8px', background: 'transparent', color: 'var(--gray-500)', border: '1px solid var(--gray-300)', borderRadius: 4, cursor: 'pointer', marginLeft: 6, fontSize: 12 }}
                          title="Discard changes"
                        >
                          <X size={12} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: 'var(--gray-500)' }}>No categories match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10, 15, 25, 0.82)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 32, cursor: 'zoom-out',
          }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPreview(null); }}
            aria-label="Close preview"
            style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: 999, width: 40, height: 40, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={20} />
          </button>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <img
              src={preview.url}
              alt={preview.name}
              style={{ maxWidth: '100%', maxHeight: '82vh', objectFit: 'contain', borderRadius: 6, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', background: '#fff' }}
            />
            <div style={{ color: '#fff', fontSize: 13, textAlign: 'center' }}>
              <div style={{ fontWeight: 600 }}>{preview.name}</div>
              <div style={{ color: '#cbd5e1', fontSize: 11, marginTop: 2, wordBreak: 'break-all', maxWidth: 800 }}>{preview.url}</div>
            </div>
          </div>
        </div>
      )}
      {blocksModal && (
        <div onClick={() => setBlocksModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,15,25,0.55)', zIndex: 9998, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 20, width: 'min(760px, 96vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>Content Blocks — {blocksModal.name}</div>
              <button type="button" onClick={() => setBlocksModal(null)} aria-label="Close" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--gray-500)' }}><X size={20} /></button>
            </div>
            <PageBlocksEditor pageType="pcp" pageKey={blocksModal.key} env={getActiveEnv()} viewUrl={blocksModal.key ? ('https://' + ({ sb1: 'the-crab-place-sandbox-1.mybigcommerce.com', sb2: 'the-crab-place-sandbox-2.mybigcommerce.com', prod: 'www.crabplace.com' }[getActiveEnv()] || '') + blocksModal.key) : null} />
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px 12px', fontWeight: 600, color: 'var(--gray-600)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 };
const td = { padding: '10px 12px', verticalAlign: 'top' };
const input = { padding: '6px 8px', width: '100%', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13 };
